/**
 * Web i18next bootstrap.
 *
 * - Bundles the EN translation eagerly so first paint is never delayed.
 * - Other 10 locales lazy-load via dynamic import on language change.
 * - Browser language detected once, then user's manual selection latches
 *   in localStorage (`tc_lang_user_set=1`).
 *
 * `setHtmlLang(lang)` updates `document.documentElement.lang` so our CSS
 * font rules can swap to the right Noto pack for Indic scripts.
 */
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import en from "@/locales/en/common.json";

// Phase 14.9.12 — interleaved globally-diverse order. English first
// (default), then alternate European/Indian/etc. so the first ~10
// picks read as global rather than India-heavy. Trailing positions
// hold the remaining Indian languages (12 of 18 total are Indian, so
// a perfect interleave is impossible — we frontload diversity).
// Underlying translation files / labels untouched; this is purely
// the picker order.
export const SUPPORTED_LANGS = [
    "en",                              // anchor
    "es", "hi", "fr", "ta", "de",      // interleave: euro / Indian / euro / Indian / euro
    "bn", "it", "te", "pt",            // continued interleave (Italian + Portuguese last euro)
    // Remaining Indian languages, by approximate speakers:
    "mr", "gu", "kn", "ml", "pa",
    "ur", "or", "as",
];

export const LANG_META = {
    en: { native: "English", english: "English" },
    hi: { native: "हिन्दी", english: "Hindi" },
    te: { native: "తెలుగు", english: "Telugu" },
    ta: { native: "தமிழ்", english: "Tamil" },
    ml: { native: "മലയാളം", english: "Malayalam" },
    kn: { native: "ಕನ್ನಡ", english: "Kannada" },
    bn: { native: "বাংলা", english: "Bengali" },
    mr: { native: "मराठी", english: "Marathi" },
    gu: { native: "ગુજરાતી", english: "Gujarati" },
    pa: { native: "ਪੰਜਾਬੀ", english: "Punjabi" },
    or: { native: "ଓଡ଼ିଆ", english: "Odia" },
    as: { native: "অসমীয়া", english: "Assamese" },
    ur: { native: "اردو", english: "Urdu" },
    fr: { native: "Français", english: "French" },
    es: { native: "Español", english: "Spanish" },
    pt: { native: "Português", english: "Portuguese" },
    it: { native: "Italiano", english: "Italian" },
    de: { native: "Deutsch", english: "German" },
};

/** Indic scripts whose languages need Noto Sans fallback. */
export const INDIC_LANGS = new Set([
    "hi", "te", "ta", "ml", "kn",
    "bn", "mr", "gu", "pa", "or", "as", "ur",
]);

const LS_KEY = "tc_lang";
const LS_USER_KEY = "tc_lang_user_set";

function pickInitial() {
    try {
        const saved = localStorage.getItem(LS_KEY);
        if (saved && SUPPORTED_LANGS.includes(saved)) return saved;
    } catch (_) { /* SSR / privacy mode */ }
    const nav = (typeof navigator !== "undefined" && navigator.language) || "en";
    const short = nav.split("-")[0].toLowerCase();
    return SUPPORTED_LANGS.includes(short) ? short : "en";
}

const LOADERS = {
    en: () => Promise.resolve({ default: en }),
    hi: () => import("@/locales/hi/common.json"),
    te: () => import("@/locales/te/common.json"),
    ta: () => import("@/locales/ta/common.json"),
    ml: () => import("@/locales/ml/common.json"),
    kn: () => import("@/locales/kn/common.json"),
    // Indian scripts without translation bundles yet — fall back to EN
    // so the locale change is a no-op but the picker still works.
    bn: () => Promise.resolve({ default: en }),
    mr: () => Promise.resolve({ default: en }),
    gu: () => Promise.resolve({ default: en }),
    pa: () => Promise.resolve({ default: en }),
    or: () => Promise.resolve({ default: en }),
    as: () => Promise.resolve({ default: en }),
    ur: () => Promise.resolve({ default: en }),
    fr: () => import("@/locales/fr/common.json"),
    es: () => import("@/locales/es/common.json"),
    pt: () => import("@/locales/pt/common.json"),
    it: () => import("@/locales/it/common.json"),
    de: () => import("@/locales/de/common.json"),
};

const initial = pickInitial();

i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
        resources: { en: { common: en } },
        lng: initial,
        fallbackLng: "en",
        defaultNS: "common",
        ns: ["common"],
        interpolation: { escapeValue: false },
        detection: {
            order: ["localStorage", "navigator"],
            lookupLocalStorage: LS_KEY,
            caches: ["localStorage"],
        },
        react: { useSuspense: false },
    });

// Eagerly load the chosen non-EN bundle on boot so first paint is localized.
async function loadIfNeeded(lang) {
    if (lang === "en" || i18n.hasResourceBundle(lang, "common")) return;
    try {
        const mod = await LOADERS[lang]();
        i18n.addResourceBundle(lang, "common", mod.default || mod, true, true);
    } catch (e) {
        console.warn(`[i18n] failed to load ${lang}`, e);
    }
}

if (initial !== "en") {
    loadIfNeeded(initial).then(() => i18n.changeLanguage(initial));
}

i18n.on("languageChanged", (lng) => {
    if (typeof document !== "undefined") {
        document.documentElement.lang = lng;
        document.documentElement.dataset.indic = INDIC_LANGS.has(lng) ? "1" : "0";
    }
});
if (typeof document !== "undefined") {
    document.documentElement.lang = initial;
    document.documentElement.dataset.indic = INDIC_LANGS.has(initial) ? "1" : "0";
}

export async function setLanguage(lang) {
    if (!SUPPORTED_LANGS.includes(lang)) return;
    await loadIfNeeded(lang);
    await i18n.changeLanguage(lang);
    try {
        localStorage.setItem(LS_KEY, lang);
        localStorage.setItem(LS_USER_KEY, "1");
    } catch (_) { /* ignore */ }
}

export default i18n;
